import type { TrackerInitOptions, UplaneApi } from '../contracts';
import type { TrackerLogger } from '../logger';

export const FRAMER_CONSENT_STORAGE_KEY = 'framerCookiesConsentMode';

export type FramerAnalyticsConsent = 'accepted' | 'pending' | 'rejected';

const DEFAULT_POLL_INTERVAL_MS = 500;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const isFramerWebsite = (documentObject: Document): boolean => {
  const generator = documentObject.querySelector<HTMLMetaElement>('meta[name="generator"]')?.content;
  return generator?.trim().toLowerCase().startsWith('framer') === true
    || documentObject.querySelector('[data-framer-root], [data-framer-hydrate-v2]') !== null;
};

export const getFramerConsentStorage = (logger: TrackerLogger): Storage | null => {
  try {
    return window.localStorage;
  } catch (error: unknown) {
    logger.debug('framer_consent_storage', {
      outcome: 'unavailable',
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

export const readFramerAnalyticsConsent = (storage: Storage | null): FramerAnalyticsConsent => {
  if (!storage) return 'pending';

  try {
    const rawConsent = storage.getItem(FRAMER_CONSENT_STORAGE_KEY);
    if (!rawConsent) return 'pending';

    const consent: unknown = JSON.parse(rawConsent);
    if (!isRecord(consent)) return 'pending';
    if (consent.analytics === true) return 'accepted';
    if (consent.analytics === false) return 'rejected';
    return 'pending';
  } catch {
    return 'pending';
  }
};

export const applyFramerConsentToOptions = (
  options: TrackerInitOptions,
  consent: FramerAnalyticsConsent
): TrackerInitOptions => consent === 'accepted'
  ? {
      ...options,
      privacyMode: 'persistent',
      policyBasis: 'opt_in'
    }
  : {
      ...options,
      privacyMode: 'anonymous',
      policyBasis: 'opt_in'
    };

type InstallFramerConsentAdapterOptions = {
  api: UplaneApi;
  initialConsent: FramerAnalyticsConsent;
  logger: TrackerLogger;
  storage: Storage | null;
  windowObject?: Window;
  pollIntervalMs?: number;
};

export type FramerConsentAdapter = {
  synchronize(): void;
  stop(): void;
};

export const installFramerConsentAdapter = ({
  api,
  initialConsent,
  logger,
  storage,
  windowObject = window,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS
}: InstallFramerConsentAdapterOptions): FramerConsentAdapter => {
  let currentConsent = initialConsent;
  let stopped = false;
  let transition = Promise.resolve();

  const enqueueTransition = (consent: FramerAnalyticsConsent): void => {
    transition = transition
      .then(async () => {
        if (stopped) return;
        if (consent === 'accepted') await api.grantConsent();
        else await api.revokeConsent();
      })
      .catch((error: unknown) => {
        logger.error('framer_consent_transition', {
          outcome: 'failed',
          consent,
          error: error instanceof Error ? error.message : String(error)
        });
      });
  };

  const synchronize = (): void => {
    const nextConsent = readFramerAnalyticsConsent(storage);
    if (nextConsent === currentConsent) return;
    currentConsent = nextConsent;
    enqueueTransition(nextConsent);
  };

  const onStorage = (event: StorageEvent): void => {
    if (event.key === null || event.key === FRAMER_CONSENT_STORAGE_KEY) synchronize();
  };

  windowObject.addEventListener('storage', onStorage);
  const pollTimer = windowObject.setInterval(synchronize, Math.max(100, pollIntervalMs));

  return {
    synchronize,
    stop() {
      if (stopped) return;
      stopped = true;
      windowObject.clearInterval(pollTimer);
      windowObject.removeEventListener('storage', onStorage);
    }
  };
};
